import React, { useState, useEffect } from "react";
import "../../assets/Css/Banners.css";
import Outlet from "../Outlet";
import { uploadImageToFirebase } from "../../utils/uploadImage";

const Banners = () => {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [message, setMessage] = useState({ text: "", type: "" });

  const [formData, setFormData] = useState({
    webImageFile: null,
    mobileImageFile: null,
    webImageUrl: "",
    mobileImageUrl: "",
    userId: "",
    productId: "",
    externalLink: "",
    isActive: true,
  });

  const [webUploadProgress, setWebUploadProgress] = useState(0);
  const [mobileUploadProgress, setMobileUploadProgress] = useState(0);

  // Show temporary message
  const showMessage = (text, type) => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: "", type: "" }), 3000);
  };

  const fetchBanners = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("adminToken");
      if (!token) {
        showMessage("Please login first.", "error");
        setBanners([]);
        return;
      }

      const res = await fetch(
        "https://network.rentel.in/banners/get?limit=20&skip=0&isActive=true",
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (!res.ok) throw new Error(`Server responded with ${res.status}`);

      const data = await res.json();
      setBanners(data?.banners || []);
    } catch (error) {
      console.error("Fetch Banners Error:", error);
      showMessage("Error fetching banners.", "error");
      setBanners([]);
    } finally {
      setLoading(false);
    }
  };

  const checkAuthStatus = () => !!localStorage.getItem("adminToken");

  useEffect(() => {
    if (!checkAuthStatus()) {
      showMessage("Please login first to manage banners.", "error");
      return;
    }
    fetchBanners();
  }, []);

  const handleInputChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    if (type === "file") {
      setFormData((prev) => ({ ...prev, [name]: files[0] }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      }));
    }
  };

  const resetForm = () => {
    setFormData({
      webImageFile: null,
      mobileImageFile: null,
      webImageUrl: "",
      mobileImageUrl: "",
      userId: "",
      productId: "",
      externalLink: "",
      isActive: true,
    });
    setEditingId(null);
    setWebUploadProgress(0);
    setMobileUploadProgress(0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!editingId && (!formData.webImageFile || !formData.mobileImageFile)) {
      showMessage(
        "Please select both web and mobile images for new banners.",
        "error"
      );
      return;
    }

    const token = localStorage.getItem("adminToken");
    if (!token) return showMessage("Please login first.", "error");

    setSubmitting(true);
    try {
      if (editingId) await handleEditBanner();
      else await handleAddBanner();
    } catch (err) {
      console.error(err);
      showMessage(err.message || "Error processing banner", "error");
    } finally {
      setSubmitting(false);
    }
  };

  // 🔄 Add Banner
  const handleAddBanner = async () => {
    try {
      const webImageUrl = await uploadImageToFirebase(
        formData.webImageFile,
        setWebUploadProgress
      );
      const mobileImageUrl = await uploadImageToFirebase(
        formData.mobileImageFile,
        setMobileUploadProgress
      );

      const payload = {
        webImage: webImageUrl,
        mobileImage: mobileImageUrl,
        isActive: formData.isActive,
      };

      if (formData.userId?.trim()) payload.userId = formData.userId.trim();
      if (formData.productId?.trim())
        payload.productId = formData.productId.trim();
      if (formData.externalLink?.trim())
        payload.externalLink = formData.externalLink.trim();

      console.log("🚀 Sending Payload:", payload);

      const res = await fetch("https://network.rentel.in/banners/add", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json();
      if (res.ok) {
        showMessage("Banner added successfully!", "success");
        resetForm();
        fetchBanners();
      } else {
        showMessage(data.message || "Failed to add banner", "error");
      }
    } catch (error) {
      console.error("❌ Add Banner Error:", error);
      showMessage("Error uploading images or network error", "error");
    }
  };

  // 🔄 Edit Banner
  const handleEditBanner = async () => {
    try {
      const token = localStorage.getItem("adminToken");
      let res, data;

      if (formData.webImageFile || formData.mobileImageFile) {
        const form = new FormData();
        if (formData.webImageFile)
          form.append("webImage", formData.webImageFile);
        if (formData.mobileImageFile)
          form.append("mobileImage", formData.mobileImageFile);
        if (formData.userId?.trim())
          form.append("userId", formData.userId.trim());
        if (formData.productId?.trim())
          form.append("productId", formData.productId.trim());
        if (formData.externalLink?.trim())
          form.append("externalLink", formData.externalLink.trim());
        form.append("isActive", formData.isActive);

        res = await fetch(`https://network.rentel.in/banners/${editingId}`, {
          method: "PUT",
          headers: { Authorization: `Bearer ${token}` },
          body: form,
        });
      } else {
        const payload = {
          ...(formData.userId?.trim()
            ? { userId: formData.userId.trim() }
            : {}),
          ...(formData.productId?.trim()
            ? { productId: formData.productId.trim() }
            : {}),
          ...(formData.externalLink?.trim()
            ? { externalLink: formData.externalLink.trim() }
            : {}),
          isActive: formData.isActive,
        };

        res = await fetch(`https://network.rentel.in/banners/${editingId}`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        });
      }

      data = await res.json();
      if (res.ok) {
        showMessage("Banner updated successfully!", "success");
        resetForm();
        fetchBanners();
      } else showMessage(data.message || "Failed to update banner", "error");
    } catch (error) {
      console.error("Edit Banner Error:", error);
      showMessage("Network error during update", "error");
    }
  };

  const handleEdit = (banner) => {
    setEditingId(banner._id);
    setFormData({
      webImageFile: null,
      mobileImageFile: null,
      webImageUrl: banner.webImage || "",
      mobileImageUrl: banner.mobileImage || "",
      userId: banner.userId?._id || "",
      productId: banner.productId?._id || "",
      externalLink: banner.externalLink || "",
      isActive: banner.isActive,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDelete = async (bannerId) => {
    if (!window.confirm("Are you sure you want to delete this banner?")) return;
    try {
      const token = localStorage.getItem("adminToken");
      const res = await fetch(`https://network.rentel.in/banners/${bannerId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok) {
        showMessage("Banner deleted successfully!", "success");
        fetchBanners();
      } else showMessage(data.message || "Failed to delete banner", "error");
    } catch (error) {
      console.error("Delete Banner Error:", error);
      showMessage("Network error during deletion", "error");
    }
  };

  return (
    <Outlet>
      <div className="container">
        {message.text && (
          <div className={`message-box show ${message.type}`}>
            {message.text}
          </div>
        )}

        {/* Banner Form */}
        <div className="form-container">
          <h2 className="text-2xl font-semibold mb-4 text-gray-700">
            {editingId ? "Edit Banner" : "Add New Banner"}
          </h2>

          <form id="banner-form" className="space-y-4" onSubmit={handleSubmit}>
            {/* Web Image */}
            <div>
              <label className="form-label">
                Web Banner Image <span className="text-red-500">*</span>
              </label>
              {editingId && formData.webImageUrl && (
                <img
                  src={formData.webImageUrl}
                  className="w-32 h-20 border mb-2"
                  alt="web"
                />
              )}
              <input
                type="file"
                name="webImageFile"
                accept="image/*"
                className="form-input"
                onChange={handleInputChange}
              />
            </div>

            {/* Mobile Image */}
            <div>
              <label className="form-label">
                Mobile Banner Image{" "}
                <span className="text-red-500">
                  *(width 435 height 200 mobile)
                </span>
              </label>
              {editingId && formData.mobileImageUrl && (
                <img
                  src={formData.mobileImageUrl}
                  className="w-20 h-32 border mb-2"
                  alt="mobile"
                />
              )}
              <input
                type="file"
                name="mobileImageFile"
                accept="image/*"
                className="form-input"
                onChange={handleInputChange}
              />
            </div>

            {/* URL Link */}
            <div>
              <label htmlFor="externalLink" className="form-label">
                URL Link
              </label>
              <input
                type="text"
                id="externalLink"
                name="externalLink"
                value={formData.externalLink}
                onChange={handleInputChange}
                className="form-input"
                placeholder="Enter URL Link"
              />
            </div>

            {/* UserId */}
            <div>
              <label className="form-label">User ID</label>
              <input
                type="text"
                name="userId"
                value={formData.userId}
                onChange={handleInputChange}
                className="form-input"
                placeholder="Enter User ID (optional)"
              />
            </div>

            {/* ProductId */}
            <div>
              <label className="form-label">Product ID</label>
              <input
                type="text"
                name="productId"
                value={formData.productId}
                onChange={handleInputChange}
                className="form-input"
                placeholder="Enter Product ID (optional)"
              />
            </div>

            {/* Active checkbox */}
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                name="isActive"
                checked={formData.isActive}
                onChange={handleInputChange}
                className="form-checkbox"
              />
              <label className="form-label-inline">Active</label>
            </div>

            {/* Buttons */}
            <div className="flex space-x-2">
              <button
                type="submit"
                className="btn-primary"
                disabled={submitting}
              >
                {submitting
                  ? "Processing..."
                  : editingId
                  ? "Update Banner"
                  : "Add Banner"}
              </button>
              {editingId && (
                <button
                  type="button"
                  onClick={resetForm}
                  className="btn-warning"
                  disabled={submitting}
                >
                  Cancel Edit
                </button>
              )}
            </div>
          </form>
        </div>

        {/* Banner Table */}
        <div className="table-container mt-6">
          <h2 className="text-2xl font-semibold mb-4 text-gray-700">
            Existing Banners
          </h2>
          {loading ? (
            <div>Loading...</div>
          ) : banners.length === 0 ? (
            <div>No banners available.</div>
          ) : (
            <div className="overflow-x-auto">
              <table id="banners-table">
                <thead>
                  <tr>
                    <th>Sr. No.</th>
                    <th>Web</th>
                    <th>Mobile</th>
                    <th>User</th>
                    <th>Product</th>
                    <th>URL Link</th>
                    <th>Active</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {banners.map((banner, index) => (
                    <tr key={banner._id}>
                      <td>{index + 1}</td>
                      <td className="ban-img-wd">
                        <img src={banner.webImage} className="ban-img" />
                      </td>
                      <td className="ban-img-wd">
                        <img src={banner.mobileImage} className="ban-img" />
                      </td>
                      <td>{banner.userId?.name || "N/A"}</td>
                      <td>{banner.productId?.title || "N/A"}</td>
                      <td>
                        {banner.externalLink ? (
                          <a
                            href={banner.externalLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 underline"
                          >
                            {banner.externalLink}
                          </a>
                        ) : (
                          "N/A"
                        )}
                      </td>
                      <td>{banner.isActive ? "Yes" : "No"}</td>
                      <td>
                        <button
                          className="btn-warning"
                          onClick={() => handleEdit(banner)}
                        >
                          Edit
                        </button>
                        <button
                          className="btn-danger"
                          onClick={() => handleDelete(banner._id)}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </Outlet>
  );
};

export default Banners;
